/**
 * The one transport for Forge api-dl endpoints: auth header, error-envelope
 * parsing, optional JSON body, optional AbortSignal, and in-flight request
 * coalescing live here so endpoint modules stay one-line descriptors.
 *
 * Wire casing follows ADR-0021: the wire stays snake_case, and this module is
 * the one place that makes it so. Callers provide camelCase request shapes;
 * `toWire` converts body keys to their snake_case wire form at serialization.
 * Query strings are still built snake_case by the endpoint functions.
 */
import { ApiService } from '../../services/api';

export class ForgeApiError extends Error {
  status: number;

  detail?: string;

  rawBody?: string;

  constructor(message: string, status: number, detail?: string, rawBody?: string) {
    super(message);
    this.name = 'ForgeApiError';
    this.status = status;
    this.detail = detail;
    this.rawBody = rawBody;
  }
}

type ApiErrorPayload = {
  detail?: string | { code?: string; message?: string };
  message?: string;
  errors?: Array<{ detail?: string | { code?: string; message?: string }; message?: string }>;
};

function normalizeApiErrorDetail(detail: ApiErrorPayload['detail']): string | undefined {
  if (!detail) return undefined;
  if (typeof detail === 'string') {
    try {
      const parsedDetail = JSON.parse(detail) as ApiErrorPayload['detail'];
      if (parsedDetail && typeof parsedDetail !== 'string') {
        return parsedDetail.message || parsedDetail.code || detail;
      }
    } catch {
      return detail;
    }
    return detail;
  }
  return detail.message || detail.code;
}

/** Statuses a proxy in front of api-dl answers when api-dl is down or too slow. */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

export async function parseApiError(response: Response, fallback: string): Promise<ForgeApiError> {
  const rawBody = await response.text().catch(() => '');
  let payload: ApiErrorPayload | null = null;

  if (rawBody) {
    try {
      payload = JSON.parse(rawBody) as ApiErrorPayload;
    } catch {
      payload = null;
    }
  }

  // A non-JSON body is shown only as plain text from api-dl itself: an HTML
  // error page or a gateway's proxy text is not a message, so the status
  // stands in for it and `rawBody` keeps it for debugging.
  const isGateway = GATEWAY_STATUSES.has(response.status);
  const readableBody =
    payload === null && (isGateway || rawBody.trimStart().startsWith('<')) ? '' : rawBody;
  const nestedError = payload?.errors?.find((error) => error.detail || error.message);
  const detail =
    normalizeApiErrorDetail(payload?.detail) ||
    payload?.message ||
    normalizeApiErrorDetail(nestedError?.detail) ||
    nestedError?.message ||
    readableBody ||
    undefined;
  // HTTP/2 responses carry no status text.
  const status = `${response.status} ${response.statusText}`.trim();
  const message =
    detail ||
    (isGateway
      ? `Server unavailable (${status}). Try again in a moment.`
      : `${fallback} (${status})`);

  return new ForgeApiError(message, response.status, detail, rawBody || undefined);
}

function filenameFromContentDisposition(value: string | null, fallback: string): string {
  if (!value) return fallback;
  const filenameStarMatch = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch {
      return fallback;
    }
  }
  const filenameMatch = value.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] ?? fallback;
}

/**
 * Convert a camelCase request key to its snake_case wire form (ADR-0021).
 * Only strict lower-camelCase identifiers are converted; keys that are
 * already snake_case, start with an uppercase letter or digit, or contain
 * anything but ASCII letters and digits (dynamic keys such as mod tags,
 * barcodes, or ids) pass through untouched.
 */
function toWireKey(key: string): string {
  if (!/^[a-z][a-zA-Z0-9]*$/.test(key) || !/[A-Z]/.test(key)) return key;
  return key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

/**
 * Deep-convert plain-object keys to the snake_case wire form; values are never
 * touched. Keys are assumed to be static field names: never put a
 * caller-controlled string in key position, because a runtime value that
 * happens to look camelCase would be converted like a field name.
 */
export function toWire(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(toWire);
  if (value !== null && typeof value === 'object' && value.constructor === Object) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [toWireKey(key), toWire(entry)])
    );
  }
  return value;
}

export type ForgeRequestOptions = {
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * JSON body. Keys are converted to the snake_case wire form via `toWire`
   * (ADR-0021), so callers provide camelCase request shapes; already-snake
   * keys pass through unchanged.
   */
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Coalesce concurrent identical requests: while a request with this
   * `[endpointName, key]` pair is in flight, later callers receive the same
   * promise. The endpoint-name segment structurally segregates the registry so
   * two endpoints can never share a key. Ignored when `signal` is set: a
   * shared promise cannot honor per-caller aborts, so abortable requests
   * always run standalone.
   */
  dedupeKey?: readonly [endpointName: string, key: string];
};

const inFlightRequests = new Map<string, Promise<unknown>>();

/** Make reads started after a successful mutation fetch fresh data. */
export function invalidateForgeRequests(endpointName: string): void {
  const prefix = `${endpointName}\u0000`;
  for (const key of inFlightRequests.keys()) {
    if (key.startsWith(prefix)) inFlightRequests.delete(key);
  }
}

/** Fetch with the Forge auth/error contract and hand back the raw Response. */
export async function forgeFetch(
  token: string,
  url: string,
  errorLabel: string,
  options: ForgeRequestOptions = {}
): Promise<Response> {
  const { method, body, signal } = options;
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const response = await fetch(url, {
    ...(method ? { method } : {}),
    headers,
    ...(body !== undefined ? { body: JSON.stringify(toWire(body)) } : {}),
    ...(signal ? { signal } : {}),
  });
  ApiService.handleError(response);
  if (!response.ok) {
    throw await parseApiError(response, errorLabel);
  }
  return response;
}

export async function forgeRequest<T>(
  token: string,
  url: string,
  errorLabel: string,
  options: ForgeRequestOptions = {}
): Promise<T> {
  const { dedupeKey } = options;
  if (dedupeKey !== undefined && !options.signal) {
    const registryKey = dedupeKey.join('\u0000');
    const existing = inFlightRequests.get(registryKey);
    if (existing) return existing as Promise<T>;
    const request = forgeFetch(token, url, errorLabel, options).then(
      (response) => response.json() as Promise<T>
    );
    inFlightRequests.set(registryKey, request);
    try {
      return await request;
    } finally {
      if (inFlightRequests.get(registryKey) === request) inFlightRequests.delete(registryKey);
    }
  }
  const response = await forgeFetch(token, url, errorLabel, options);
  return response.json();
}

export async function forgeVoidRequest(
  token: string,
  url: string,
  errorLabel: string,
  options: ForgeRequestOptions = {}
): Promise<void> {
  await forgeFetch(token, url, errorLabel, options);
}

export async function forgeBlobRequest(
  token: string,
  url: string,
  errorLabel: string,
  fallbackFilename: string,
  options: ForgeRequestOptions = {}
): Promise<{ blob: Blob; filename: string }> {
  const response = await forgeFetch(token, url, errorLabel, options);
  const blob = await response.blob();
  const filename = filenameFromContentDisposition(
    response.headers.get('content-disposition'),
    fallbackFilename
  );
  return { blob, filename };
}
